import {Component, OnInit} from '@angular/core';
import {LoanApplicationCloningRequest} from '../../../model/loan-application/loan-application-cloning-request.model';
import {CreditAssessmentService} from '../../../service/credit-assessment.service';
import {PreApprovedLoanManualCreditAssessment} from '../../../model/eligibility-check/pre-approved-loan-manual-credit-assessment.model';
import {ActivatedRoute, Router} from '@angular/router';
import {LoanApplicationService} from '../../../service/loan-application.service';
import {LoanApplication} from '../../../model/loan-application.model';
import {ConfigService} from '../../../service/config.service';
import {ChannelPartnerService} from '../../../service/channel-partner.service';
import {UserAddress} from '../../../model/user-address.model';
import {UserAddressService} from '../../../service/user-address.service';
import {BankAccountService} from '../../../service/bank-account.service';
import {BankAccount} from '../../../model/bank-account';
import {MatDialog} from '@angular/material';
import {UpdateUserAddressComponent} from '../../../user/user-address/update-user-address/update-user-address.component';
import {CommonUtil} from '../../../utils/common.util';
import {UpdateBankAccountComponent} from '../../../user/bank-details/update-bank-account/update-bank-account.component';
import {Toaster, Type} from '../../../toaster/Toaster';
import {LoanApplicationSource} from '../../../model/loan-application/loan-application-source.model';
import {MultipleLoanEligibilityRule} from '../../../model/eligibility-check/multiple-loan-eligibility-check.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ChannelPartner, ChannelPartnerGroupType} from '../../../model/channel-partner.model';


@Component({
  selector: 'app-pre-approved-loan-offer',
  templateUrl: './pre-approved-loan-offer.component.html',
  styleUrls: ['./pre-approved-loan-offer.component.css']
})
export class PreApprovedLoanOfferComponent implements OnInit {

  formGroup: FormGroup;
  userId: string;
  preApprovedLoanOffer: PreApprovedLoanManualCreditAssessment;
  loanApplicationCloningRequest: LoanApplicationCloningRequest;
  userAddress: UserAddress;
  bankAccount: BankAccount;
  minimumLoanAmount: number;
  loanAmountDenomination: number;
  isLoading = false;
  isValid = true;
  editMode: boolean;
  eligibleTenures: number[];
  isSubventedPreApprovalChannelPartner: boolean;

  constructor(private router: Router, private formBuilder: FormBuilder, private activatedRoute: ActivatedRoute, private matDialog: MatDialog, private configService: ConfigService,
              private channelPartnerService: ChannelPartnerService, private creditAssessmentService: CreditAssessmentService,
              private loanApplicationService: LoanApplicationService, private userAddressService: UserAddressService,
              private bankAccountService: BankAccountService) {

    this.activatedRoute.data.subscribe(data => {
      this.userId = data.userId;
    });
    this.formGroup = formBuilder.group({
      loanAmount: formBuilder.control(null, [Validators.required]),
      loanAmountSlider: formBuilder.control(null),
      tenure: formBuilder.control(null, [Validators.required])
    });
  }

  async ngOnInit(): Promise<void> {
    try {
      this.load(true);
      this.setEditMode(true);
      this.minimumLoanAmount = this.configService.getPreApprovedLoanOfferMinimumLoanAmount();
      this.loanAmountDenomination = this.configService.getPreApprovedLoanAmountDenomination();
      await this.checkForApprovedLoanOfferForUser(this.userId);
    } catch (error) {
      Toaster.toast('Something went wrong while trying to load pre-approved loan offer', Type.Error);
    } finally {
      this.load(false);
    }
  }

  validateLoanAmount() {
    this.isValid = this.loanApplicationCloningRequest.loanAmount >= this.minimumLoanAmount &&
      this.loanApplicationCloningRequest.loanAmount <= this.preApprovedLoanOffer.loanAmount && this.isMultipleOfZeroOne(this.loanApplicationCloningRequest.loanAmount);
    this.setEligibleTenures(null, this.isValid ? this.loanApplicationCloningRequest.loanAmount : null);
  }

  isMultipleOfZeroOne(value: number) {
    const roundedValue = (Math.round(value * 100) / 100).toFixed(2);
    return parseFloat(roundedValue) === value;
  }

  openUpdateAddressPopUp() {
    this.matDialog.open(UpdateUserAddressComponent, { data: { userId: this.userId } }).afterClosed().subscribe(async response => {
      if (!CommonUtil.isEmpty(response) && !CommonUtil.isEmpty(response.data) && !CommonUtil.isEmpty(response.data.addressId)) {
        this.userAddress = await this.userAddressService.get(response.data.addressId);
        this.loanApplicationCloningRequest.addressId = response.data.addressId;
        this.markProcessingRequiredForLoanApplicationCloning();
      }
    });
  }

  openUpdateBankAccountDetailsPopUp() {
    this.matDialog.open(UpdateBankAccountComponent, { data: { userId: this.userId } }).afterClosed().subscribe(async response => {
      if (!CommonUtil.isEmpty(response) && !CommonUtil.isEmpty(response.data) && !CommonUtil.isEmpty(response.data.bankAccount)) {
        this.bankAccount = response.data.bankAccount;
        this.loanApplicationCloningRequest.bankAccountId = response.data.bankAccount._id;
        this.markProcessingRequiredForLoanApplicationCloning();
      }
    });
  }

  async cloneLoanApplication(): Promise<void> {
    try {
      this.load(true);
      const createdClonedLoanApplicationRequest = await this.loanApplicationService.cloneLoanApplication(this.loanApplicationCloningRequest);
      await this.creditAssessmentService.availPreApprovedLoanOffer(this.preApprovedLoanOffer.id,
          {loanApplicationId: createdClonedLoanApplicationRequest.clonedLoanApplicationId});
      const msg = !this.loanApplicationCloningRequest.consentRequired ? 'Congratulations! your loan application is approved & listed for funding' :
          'Congratulations! your loan application is submitted. Since you have changed your address/ bank account, the KYC would be required to be done before approval';

      if (!createdClonedLoanApplicationRequest.consentRequired) {
        await this.router.navigate([`borrow/dashboard/applications`]);
      } else {
        await this.router.navigate([`/borrow/application/${createdClonedLoanApplicationRequest.clonedLoanApplicationId}/status`]);
      }
      Toaster.toast(msg, Type.Success);
    } catch (error) {
      Toaster.toast('Something went wrong while trying to apply your pre-approved loan, please try again in sometime', Type.Error);
    } finally {
      this.load(false);
    }
  }

  setEditMode(mode: boolean): void {
    this.editMode = mode;
  }

  async backButtonNavigate(): Promise<void> {
    if (this.editMode) {
      await this.router.navigate([`borrow/dashboard/applications`]);
    } else {
      this.setEditMode(true);
    }
  }

  private async checkForApprovedLoanOfferForUser(userId: string): Promise<void> {
    const multipleLoanEligibilityForPreApprovedLoanOffer = await this.loanApplicationService.getMultipleLoanEligibilityForUser(userId, LoanApplicationSource.PRE_APPROVAL);
    if (multipleLoanEligibilityForPreApprovedLoanOffer && multipleLoanEligibilityForPreApprovedLoanOffer.isEligible) {
      const offers = await this.creditAssessmentService.getPreApprovedLoanOffersForUser(userId, false);
      if (offers && offers.length > 0) {
        this.preApprovedLoanOffer = offers[0];
        this.preApprovedLoanOffer.loanAmount = multipleLoanEligibilityForPreApprovedLoanOffer.checks
            .find(check => check.rule === MultipleLoanEligibilityRule.OUTSTANDING_PRINCIPAL_LIMIT).eligibleLoanAmount;

        const latestLoanApplication = await this.getLatestLoanApplicationOfUser(userId);

        let preApprovedLoanChannelPartner: ChannelPartner;
        if (latestLoanApplication.partnerId) {
          const channelPartner = await this.channelPartnerService.getPartnerDetails(latestLoanApplication.partnerId);
          this.isSubventedPreApprovalChannelPartner = await this.isASubventedPreApprovalChannelPartner(channelPartner);
          if (this.isSubventedPreApprovalChannelPartner) {
            preApprovedLoanChannelPartner = channelPartner;
            this.formGroup.removeControl('tenure');
          } else {
            preApprovedLoanChannelPartner = await this.getDefaultPreApprovedChannelPartner();
          }
        } else {
          preApprovedLoanChannelPartner = await this.getDefaultPreApprovedChannelPartner();
        }

        this.userAddress = await this.userAddressService.get(latestLoanApplication.addressId);
        this.bankAccount = await this.bankAccountService.get(latestLoanApplication.bankAccountId);

        this.loanApplicationCloningRequest = {
          loanApplicationId: latestLoanApplication.id,
          loanAmount: this.preApprovedLoanOffer.loanAmount,
          tenureInMonths: this.isSubventedPreApprovalChannelPartner ? this.preApprovedLoanOffer.getPreferredTenure() : null,
          partnerId: preApprovedLoanChannelPartner.id,
          dcdScore: this.preApprovedLoanOffer.dcdScore,
          processingFeePercentage: null,
          submissionRequired: false,
          consentRequired: false,
          acceptanceRequired: false,
          source: LoanApplicationSource.PRE_APPROVAL
        };
        this.setEligibleTenures(this.loanApplicationCloningRequest.tenureInMonths, this.preApprovedLoanOffer.loanAmount);
      }
    }
  }

  private async getLatestLoanApplicationOfUser(userId: string): Promise<LoanApplication> {
    const allLoanApplications = (await this.loanApplicationService.getLoanApplicationsByUserId(userId))
        .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    return allLoanApplications[0];

  }

  private async isASubventedPreApprovalChannelPartner(channelPartner: ChannelPartner): Promise<boolean> {
    if (!CommonUtil.isEmpty(channelPartner.partnerGroupIds) && channelPartner.partnerGroupIds.length > 0) {
      const channelPartnerGroupForSubventedPreApproval = await this.channelPartnerService.getChannelPartnerGroupByType(ChannelPartnerGroupType.SubventedPreApproval);
      const subventedPreApprovalChannelPartner = channelPartner.partnerGroupIds.find(id => id === channelPartnerGroupForSubventedPreApproval._id);
      return !CommonUtil.isEmpty(subventedPreApprovalChannelPartner);
    } else {
      return false;
    }
  }

  private async getDefaultPreApprovedChannelPartner(): Promise<ChannelPartner> {
    return await this.channelPartnerService.getPartnerByName(this.configService.getPreApprovedLoanPartnerName());
  }

  private markProcessingRequiredForLoanApplicationCloning() {
    this.loanApplicationCloningRequest.consentRequired = true;
    this.loanApplicationCloningRequest.acceptanceRequired = true;
  }

  private load(loading: boolean): void {
    this.isLoading = loading;
  }

  private setEligibleTenures(tenureInMonths: number, loanAmount: number) {
    if (!this.isSubventedPreApprovalChannelPartner) {
      this.loanApplicationCloningRequest.tenureInMonths = tenureInMonths;
      this.eligibleTenures = loanAmount ? this.configService.getEligibleLoanTenuresForLoanAmount(loanAmount) : [];
    } else {
      this.eligibleTenures = [];
    }
  }
}
